'use client'

import {
  animate,
  AnimatePresence,
  motion,
  stagger,
  useReducedMotion,
  type Variants,
} from 'framer-motion'
import { useTranslations } from 'next-intl'
import { type ReactNode, useId, useLayoutEffect, useMemo, useRef } from 'react'

import { SETTING_TAB, SettingTab } from '@/lib/configs/setting-config'
import { cn } from '@/lib/utils'

// The prototype's springs: panels on the soft one, indicators on the snappier one.
const PANEL_SPRING = { type: 'spring', stiffness: 320, damping: 32 } as const
const INDICATOR_SPRING = { type: 'spring', stiffness: 520, damping: 38, mass: 0.7 } as const
const ITEM_EASE = [0.2, 0.8, 0.2, 1] as const
const EXIT_EASE = [0.4, 0, 1, 1] as const
/** Only the outermost marked blocks rise in — a row section's own header rides along with it. */
const ITEM_SELECTOR = '[data-settings-item]:not([data-settings-item] [data-settings-item])'

/** Tab labels, shared by the dialog's nav, the page's tab bar and the phone strip. */
export function useSettingTabLabels(): Record<SettingTab, string> {
  const t = useTranslations('common')
  return useMemo(
    () => ({
      [SettingTab.GENERAL]: t('general'),
      [SettingTab.NOTIFICATIONS]: t('notifications'),
      [SettingTab.SECURITY]: t('security'),
      [SettingTab.DEVICES]: t('devices'),
      [SettingTab.API]: t('api'),
      [SettingTab.BILLING]: t('billing'),
      [SettingTab.ACCOUNT]: t('accounts'),
    }),
    [t]
  )
}

/** +1 when `tab` sits after the previously shown one in SETTING_TAB, -1 when before. */
export function useTabDirection(tab: SettingTab): 1 | -1 {
  const previousRef = useRef(tab)
  const directionRef = useRef<1 | -1>(1)
  if (previousRef.current !== tab) {
    const order = SETTING_TAB.map((item) => item.id)
    directionRef.current = order.indexOf(tab) >= order.indexOf(previousRef.current) ? 1 : -1
    previousRef.current = tab
  }
  return directionRef.current
}

/** Rises the panel's rows in one after another, right after it mounts and before it paints. */
function StaggeredItems({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || reduceMotion) return
    const items = Array.from(el.querySelectorAll<HTMLElement>(ITEM_SELECTOR))
    if (!items.length) return
    // Hidden before the first paint, so nothing flashes in ahead of its turn.
    for (const item of items) item.style.opacity = '0'
    const controls = animate(
      items,
      { opacity: [0, 1], y: [8, 0] },
      { duration: 0.32, ease: ITEM_EASE, delay: stagger(0.03) }
    )
    return () => {
      controls.stop()
      for (const item of items) item.style.opacity = ''
    }
  }, [reduceMotion])
  return <div ref={ref}>{children}</div>
}

interface SettingsPanelTransitionProps {
  /** Changes when the tab does; each new key plays the transition. */
  panelKey: string
  direction: 1 | -1
  /** `y` for a vertical tab list (the dialog), `x` for a horizontal one. */
  axis?: 'x' | 'y'
  /**
   * Play the old panel out before the new one comes in. Off for route-driven tabs: an exiting
   * App Router child would already render the new route's content.
   */
  withExit?: boolean
  className?: string
  children: ReactNode
}

/**
 * A Settings tab's content changing: the new panel slides in from the side of the tab that
 * was picked (prototype Workspace drawer: ±14px with a fade, on the soft spring), then its
 * rows rise in one by one.
 */
export function SettingsPanelTransition({
  panelKey,
  direction,
  axis = 'y',
  withExit = true,
  className,
  children,
}: SettingsPanelTransitionProps) {
  const reduceMotion = useReducedMotion()
  const offset = reduceMotion ? 0 : 14
  const shift = (distance: number) => (axis === 'x' ? { x: distance } : { y: distance })
  const variants: Variants = {
    enter: (d: number) => ({ opacity: 0, ...shift(d * offset) }),
    center: { opacity: 1, x: 0, y: 0 },
    exit: (d: number) => ({
      opacity: 0,
      ...shift(d * -offset * 0.6),
      transition: { duration: 0.12, ease: EXIT_EASE },
    }),
  }
  const panel = (
    <motion.div
      key={panelKey}
      custom={direction}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ ...PANEL_SPRING, opacity: { duration: 0.2 } }}
      className={className}
    >
      <StaggeredItems>{children}</StaggeredItems>
    </motion.div>
  )
  if (!withExit) return panel
  return (
    <AnimatePresence mode="wait" initial={false} custom={direction}>
      {panel}
    </AnimatePresence>
  )
}

interface SettingsTabBarProps {
  activeTab: SettingTab
  onSelect: (tab: SettingTab) => void
  className?: string
}

/**
 * Settings' sections as a horizontal tab row (the full page and the phone dialog): icon and
 * label, with an underline that slides to the picked tab (prototype `.useg`).
 */
export function SettingsTabBar({ activeTab, onSelect, className }: SettingsTabBarProps) {
  const labels = useSettingTabLabels()
  const indicatorId = `settings-tab-indicator-${useId()}`
  return (
    <div
      role="tablist"
      className={cn(
        'border-foreground/10 flex items-end gap-1 overflow-x-auto border-b [scrollbar-width:none]',
        className
      )}
    >
      {SETTING_TAB.map((item) => {
        const active = item.id === activeTab
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(item.id)}
            className={cn(
              'relative flex h-10 shrink-0 cursor-pointer items-center gap-2 px-3 text-sm font-medium tracking-[-0.02em] transition-[color,scale] duration-150 motion-safe:active:scale-[0.97]',
              active ? 'text-foreground' : 'text-foreground/50 hover:text-foreground/80'
            )}
          >
            {active && (
              <motion.span
                layoutId={indicatorId}
                transition={INDICATOR_SPRING}
                aria-hidden="true"
                className="bg-foreground absolute inset-x-3 -bottom-px h-[1.5px] rounded-full"
              />
            )}
            <item.icon className="size-4 shrink-0" />
            <span>{labels[item.id]}</span>
          </button>
        )
      })}
    </div>
  )
}

interface SettingsNavProps {
  activeTab: SettingTab
  onSelect: (tab: SettingTab) => void
}

/** The dialog's vertical section list, its active fill sliding from item to item. */
export function SettingsNav({ activeTab, onSelect }: SettingsNavProps) {
  const labels = useSettingTabLabels()
  const fillId = `settings-nav-fill-${useId()}`
  return (
    <div className="flex flex-col gap-3">
      {SETTING_TAB.map((item) => {
        const active = item.id === activeTab
        return (
          <button
            key={item.id}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => onSelect(item.id)}
            className={cn(
              'relative flex w-45 cursor-pointer items-center gap-2 px-4 py-2 text-sm tracking-tight transition-[color,background-color,scale] duration-150',
              active
                ? 'text-foreground cursor-default'
                : 'text-foreground/80 hover:bg-foreground/3 hover:text-foreground motion-safe:active:scale-[0.98]'
            )}
          >
            {active && (
              <motion.span
                layoutId={fillId}
                transition={INDICATOR_SPRING}
                aria-hidden="true"
                className="bg-foreground/5 absolute inset-0"
              />
            )}
            <item.icon className="relative size-4 shrink-0" />
            <span className="relative">{labels[item.id]}</span>
          </button>
        )
      })}
    </div>
  )
}
